import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Navigation from "../components/Navigation";
import HomeOverlay from "../components/HomeOverlay";
import ParallaxHeader from "../components/ParallaxHeader";
import Footer from "../components/Footer";
import { useScrolled, useFadeIn } from "../hooks";
import "../styles/home.css";

const projectGroups = [
  {
    title: "Games",
    data: [
      {
        label: "SWITCH LANE",
        bg: "/assets/img/project15.png",
        path: "/games/switch-lane",
        genre: "Endless Runner",
        duration: "Nov 2026",
      },
      {
        label: "STACK UP",
        bg: "/assets/img/project14.png",
        path: "/games/stack-up",
        genre: "Arcade",
        duration: "Oct 2026",
      },
      {
        label: "CLAPPING BIRD",
        bg: "/assets/img/project13.png",
        path: "/games/clapping-bird",
        genre: "Arcade",
        duration: "Oct 2026",
      },
      {
        label: "THE HEGEMONS",
        bg: "/assets/img/project12.png",
        path: "/games/the-hegemons",
        genre: "Action",
        duration: "Jun 2026 - Oct 2026",
      },
      {
        label: "CANDY BOOM",
        bg: "/assets/img/project10.png",
        path: "/games/candy-boom",
        genre: "Puzzle",
        duration: "May 2026",
      },
      {
        label: "3X3 ARENA",
        bg: "/assets/img/project8.png",
        path: "/games/3x3-arena",
        genre: "Action",
        duration: "Apr 2026",
      },
    ],
  },
];

const HomePage = () => {
  const navigate = useNavigate();
  const scrolled = useScrolled(500);
  const trackRefs = useRef([]);
  const [projectsRef, projectsVisible] = useFadeIn(0);

  useEffect(() => {
    const tracks = trackRefs.current;

    tracks.forEach((track) => {
      if (!track) return;

      let isDown = false;
      let startX;
      let scrollLeft;

      const onDown = (e) => {
        isDown = true;
        track.style.cursor = "grabbing";
        startX = e.pageX - track.offsetLeft;
        scrollLeft = track.scrollLeft;
      };

      const onLeave = () => {
        isDown = false;
        track.style.cursor = "grab";
      };

      const onUp = () => {
        isDown = false;
        track.style.cursor = "grab";
      };

      const onMove = (e) => {
        if (!isDown) return;
        e.preventDefault();
        track.scrollLeft =
          scrollLeft - (e.pageX - track.offsetLeft - startX) * 1.5;
      };

      track.addEventListener("mousedown", onDown);
      track.addEventListener("mouseleave", onLeave);
      track.addEventListener("mouseup", onUp);
      track.addEventListener("mousemove", onMove);

      return () => {
        track.removeEventListener("mousedown", onDown);
        track.removeEventListener("mouseleave", onLeave);
        track.removeEventListener("mouseup", onUp);
        track.removeEventListener("mousemove", onMove);
      };
    });
  }, []);

  return (
    <main id="home" className={scrolled ? "scrolled" : ""}>
      <HomeOverlay />
      <Navigation isHome />

      <ParallaxHeader />

      <section className="project-section" ref={projectsRef}>
        {projectGroups.map((group, groupIndex) => (
          <div key={groupIndex} className="project-group">
            <div
              className={`clients-header fade-in${projectsVisible ? " appear" : ""}`}
            >
              <h2>{group.title}</h2>
              <div className="clients-line" />
            </div>

            <div className="clients-scroll-wrap">
              <div
                className="clients-track"
                ref={(el) => (trackRefs.current[groupIndex] = el)}
              >
                {group.data.map((proj, idx) => (
                  <div
                    key={idx}
                    className="client-tile fade-in appear"
                    onClick={() => navigate(proj.path)}
                  >
                    <div className="client-image-wrapper">
                      <div
                        className="client-image"
                        style={{ backgroundImage: `url('${proj.bg}')` }}
                      />
                      <div className="client-hover-overlay">
                        <div className="view-project-btn">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            width="20"
                            height="20"
                          >
                            <polyline
                              points="9 5 19 5 19 15"
                              fill="none"
                              stroke="#fff"
                              strokeWidth="2"
                              strokeMiterlimit="10"
                            />
                            <line
                              x1="19"
                              y1="5"
                              x2="6"
                              y2="18"
                              fill="none"
                              stroke="#fff"
                              strokeWidth="2"
                              strokeMiterlimit="10"
                            />
                          </svg>
                          View Game
                        </div>
                      </div>
                    </div>

                    <div className="client-info">
                      <div className="client-label">{proj.label}</div>

                      <div className="client-meta">
                        <span className="client-genre">{proj.genre}</span>
                        <span className="client-dot" />
                        <span className="client-duration">
                          {proj.duration}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </section>

      <section className="about-section">
  <div className="about-media">
    <div
      className="background-image"
      style={{
        background: "url('/assets/img/about1.png') center center / cover",
      }}
    />
    <div className="bottom-fade" />
  </div>

  <div className="wrap-text">
    <p className="about-text">
      I&apos;m Tung, a Game Developer focused on engaging gameplay. I work
      mainly in Unity and C#, concentrating on action and puzzle games —
      from core mechanics and game feel to the small details that make
      players want &quot;one more run&quot;.
    </p>

    <p className="about-text">
      I care about how games feel: tight controls, readable mechanics, and
      systems that reward curiosity. My strength is turning a simple idea
      into a polished, playable loop — prototyping fast and iterating on
      feedback. Right now I&apos;m pushing deeper into combat design, level
      pacing, and clean, reusable code.
    </p>

    <p className="about-text">
      Long term, I want to grow into a professional Game Developer,
      contribute to impactful titles, and eventually lead my own projects —
      building sharper skills and games players genuinely remember.
    </p>

    <button
      type="button"
      className="button about-btn"
      style={{ "--clr": "#1a1a1a" }}
      onClick={() => navigate("/about")}
    >
      <span className="button__icon-wrapper">
        <svg
          viewBox="0 0 14 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="button__icon-svg"
          width="10"
        >
          <path
            d="M13.376 11.552l-.264-10.44-10.44-.24.024 2.28 6.96-.048L.2 12.56l1.488 1.488 9.432-9.432-.048 6.912 2.304.024z"
            fill="currentColor"
          />
        </svg>

        <svg
          viewBox="0 0 14 15"
          fill="none"
          width="10"
          xmlns="http://www.w3.org/2000/svg"
          className="button__icon-svg button__icon-svg--copy"
        >
          <path
            d="M13.376 11.552l-.264-10.44-10.44-.24.024 2.28 6.96-.048L.2 12.56l1.488 1.488 9.432-9.432-.048 6.912 2.304.024z"
            fill="currentColor"
          />
        </svg>
      </span>
      More About Me
    </button>
  </div>
</section>

      <Footer />
    </main>
  );
};

export default HomePage;